import { ServiceAction } from "../service/service-part-action"

export type ComparisonDirection = "UP" | "DOWN" | "UNCHANGED"
export type ComparisonSentiment = "POSITIVE" | "NEGATIVE" | "NEUTRAL"

export interface PeriodComparison {
  /** The metric in the compared period; null when it has no data. */
  value: number | null
  /** Unsigned; null when there is nothing to compare with. */
  changePercentage: number | null
  direction: ComparisonDirection
  /** Whether the change is good news; a longer lead time is not. */
  sentiment: ComparisonSentiment
}

export const compareToPeriod = (
  current: number | null,
  past: number | null,
  higherIsBetter: boolean,
): PeriodComparison => {
  if (current === null || past === null || past === 0) {
    return {
      value: past,
      changePercentage: null,
      direction: "UNCHANGED",
      sentiment: "NEUTRAL",
    }
  }
  const changePercentage = Math.round((Math.abs(current - past) / past) * 100)
  if (changePercentage === 0) {
    return {
      value: past,
      changePercentage,
      direction: "UNCHANGED",
      sentiment: "NEUTRAL",
    }
  }
  const increased = current > past
  return {
    value: past,
    changePercentage,
    direction: increased ? "UP" : "DOWN",
    sentiment: increased === higherIsBetter ? "POSITIVE" : "NEGATIVE",
  }
}

export interface MetricComparison {
  previousPeriod: PeriodComparison
  samePeriodLastYear: PeriodComparison
}

/**
 * Whole calendar periods compare with the one before, others by day count.
 * `isCurrent` is whether the whole period is the ongoing one, which decides
 * between "Förra månaden" and "Månaden dessförinnan".
 */
export type ComparedPeriod =
  | { type: "WEEK" | "MONTH" | "YEAR"; isCurrent: boolean }
  | { type: "DAYS"; dayCount: number }

/** A part action, or a manual action when `serviceAction` is null. */
export interface StatsAction {
  key: string
  name: string
  serviceAction: ServiceAction | null
}

export interface StatsActionCount extends StatsAction {
  count: number
}

export interface StatsCountShare {
  total: number
  percentage: number
}

export interface RevenueStats {
  labor: StatsCountShare
  products: StatsCountShare
  total: number
}

export type AddonSource = "INSPECTION" | "ADDON_ORDER"

/** Routine actions the addon cards leave out until someone selects them. */
const ACTIONS_DESELECTED_BY_DEFAULT: ServiceAction[] = [
  ServiceAction.CHECK,
  ServiceAction.ADJUST,
]

/** What the addon cards filter on: an action on any part, or a manual action. */
export type AddonFilterChoice =
  | { key: string; serviceAction: ServiceAction }
  | { key: string; serviceAction: null; name: string }

export type AddonFilterAction = AddonFilterChoice & { count: number }

export const toAddonFilterChoice = (action: StatsAction): AddonFilterChoice =>
  action.serviceAction === null
    ? { key: action.key, serviceAction: null, name: action.name }
    : { key: action.serviceAction, serviceAction: action.serviceAction }

/**
 * Whether the choice counts on an addon card. `toggledActionKeys` holds the
 * choices flipped away from their default, so one can't be selected in a
 * period and fall back to its default in the compared ones.
 */
export const isAddonActionSelected = (
  choice: AddonFilterChoice,
  toggledActionKeys: string[],
): boolean => {
  const isSelectedByDefault =
    choice.serviceAction === null ||
    !ACTIONS_DESELECTED_BY_DEFAULT.includes(choice.serviceAction)
  return isSelectedByDefault !== toggledActionKeys.includes(choice.key)
}

export type AddonDeniedRate = "HIGH" | "LOW" | "ON_TARGET"

export interface AddonActionStats extends StatsAction {
  acceptedCount: number
  deniedCount: number
}

export interface AddonStats {
  accepted: StatsCountShare
  denied: StatsCountShare
  /** Urgent work had to be done now; all manual actions count as urgent. */
  acceptedUrgent: StatsCountShare
  deniedUrgent: StatsCountShare
  /** Preventive work could wait six months. */
  acceptedPreventive: StatsCountShare
  deniedPreventive: StatsCountShare
  total: number
  actions: AddonActionStats[]
  /** Every choice of the source, selected or not, for the card's filter. */
  selectableActions: AddonFilterAction[]
  /**
   * The denied share against the target, of the urgent actions on inspections
   * and of all answers on addon orders; null without answers.
   */
  deniedRate: AddonDeniedRate | null
}

export interface UploadedBikesStats {
  total: number
  newBikes: number
  secondHandBikes: number
}

/** Medians in seconds; null when no job in the period has a value. */
export interface LeadTimeStats {
  handInToCompletedSeconds: number | null
  completedToDeliveredSeconds: number | null
  inspectionResponseSeconds: number | null
  addonOrderResponseSeconds: number | null
  customerMessageReplySeconds: number | null
}

export type LeadTimeMetric =
  | "handInToCompleted"
  | "completedToDelivered"
  | "inspectionResponse"
  | "addonOrderResponse"
  | "customerMessageReply"

/** Where a store's median lands among the other Alfred stores. */
export interface LeadTimeStanding {
  /** The share of the other stores with a longer lead time, never 0. */
  percentage: number
  /** Positive once the store beats more than half of them. */
  sentiment: ComparisonSentiment
}

/** Null when there isn't enough data for a fair comparison. */
export type LeadTimeStandings = Record<LeadTimeMetric, LeadTimeStanding | null>

export interface AddonCardStats {
  stats: AddonStats
  comparison: MetricComparison
}

export interface StatsComparisons {
  revenue: MetricComparison
  avgOrder: MetricComparison
  jobs: MetricComparison
  handInToCompleted: MetricComparison
  completedToDelivered: MetricComparison
  inspectionResponse: MetricComparison
  addonOrderResponse: MetricComparison
  customerMessageReply: MetricComparison
  topAction: {
    previousPeriod: StatsAction | null
    samePeriodLastYear: StatsAction | null
  }
}
